import { httpsCallable } from "firebase/functions";

import { auth, functions } from "@/firebase";

let initialAuthStatePromise = null;

const observeInitialAuthState = () => {
  if (!initialAuthStatePromise) {
    initialAuthStatePromise = new Promise((resolve) => {
      let unsubscribe = () => {};

      const settle = (user) => {
        unsubscribe();
        resolve(user || null);
      };

      unsubscribe = auth.onAuthStateChanged(settle, () => settle(null));
    });
  }

  return initialAuthStatePromise;
};

/**
 * Wait for Firebase to resolve its persisted auth state on a direct page load.
 * The returned user is always read from the current Auth instance so a cached
 * initial promise cannot retain a user across a later sign-in or sign-out.
 */
export const waitForInitialAuthState = async () => {
  if (!auth.currentUser) {
    await observeInitialAuthState();
  }

  return auth.currentUser || null;
};

/**
 * Ask the server for the minimal super-admin access fact. The callable derives
 * identity from Firebase Authentication and verifies the protected admin
 * registry; the browser cannot supply a UID or role.
 */
export const requestSuperAdminAccess = async (
  functionsInstance = functions,
  callableFactory = httpsCallable,
) => {
  const response = await callableFactory(
    functionsInstance,
    "getSuperAdminAccess",
  )({});
  return response?.data?.isSuperAdmin === true;
};

/**
 * Resolve the current user's server-verified super-admin access.
 * Access fails closed, and an auth transition during the request invalidates
 * the result so one account cannot inherit another account's authorization.
 */
export const currentUserIsSuperAdmin = async (
  checkAccess = requestSuperAdminAccess,
) => {
  try {
    const user = await waitForInitialAuthState();
    if (!user?.uid) return false;

    const expectedUid = user.uid;
    const hasAccess = await checkAccess();

    return (
      auth.currentUser?.uid === expectedUid &&
      hasAccess === true
    );
  } catch (error) {
    const expectedDenial = new Set([
      "functions/permission-denied",
      "functions/unauthenticated",
      "permission-denied",
      "unauthenticated",
    ]).has(error?.code);
    if (!expectedDenial) {
      console.warn("Unable to verify super-admin access", error);
    }
    return false;
  }
};

export const routeRequiresSuperAdmin = (to) => {
  return (
    to?.meta?.requiresSuperAdmin === true ||
    (Array.isArray(to?.matched) &&
      to.matched.some(
        (record) => record.meta?.requiresSuperAdmin === true,
      ))
  );
};

/**
 * Vue Router guard for super-admin-only routes.
 * The optional checker keeps the guard independently testable.
 */
export const superAdminRouteGuard = async (
  to,
  checkAccess = currentUserIsSuperAdmin,
) => {
  if (!routeRequiresSuperAdmin(to)) return true;

  try {
    return (await checkAccess())
      ? true
      : { name: "access-denied", query: { from: to.fullPath } };
  } catch (error) {
    console.warn("Unable to guard super-admin route", error);
    return { name: "access-denied", query: { from: to.fullPath } };
  }
};
